import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { INSTALLMENT_STATUS } from './installment.enums';
import { installmentBillingFilters, installmentFilters } from './installment.filters';
import { INSTALLMENT_PATH } from './installment.search';

const BILLING_PATH = '/gestao-carteira/cobrancas';
const emptyInput = z.strictObject({});
const rowInput = z.strictObject({ id: z.string() });
const billingInput = z.strictObject({ billingId: z.string() });
const preview = () => ({
  title: t`Confirmar operação do parcelamento`,
  detail: t`Confira os dados e confirme na tela. Nada é enviado sem a confirmação da pessoa.`,
});

// ---- Lista ----

export const installmentOpen = action({
  id: 'installment.open',
  kind: 'navigate',
  label: msg`Abrir parcelamento`,
  description: msg`Abre um parcelamento da lista: dados, réguas, taxas e as cobranças dele. Nada muda só por abrir.`,
  input: rowInput,
  run: ({ nav, input }) => nav.to(`${INSTALLMENT_PATH}/${input.id}`),
});
export const installmentPage = listPageAction('installment.page');
export const installmentFilter = filterAction('installment.filter', installmentFilters);
export const installmentCreate = action({
  id: 'installment.create',
  kind: 'write',
  label: msg`Novo parcelamento`,
  description: msg`Abre o Novo parcelamento: carteira, pagador, réguas, juros e multa e depois as parcelas. Nada é criado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'new', row: undefined }),
});
export const installmentCancelFromList = action({
  id: 'installment.cancelFromList',
  kind: 'draft',
  label: msg`Cancelar`,
  description: msg`Abre o cancelamento de um parcelamento da lista, sem sair dela. Nada é cancelado sem a confirmação da pessoa.`,
  input: rowInput,
  run: ({ nav, input }) => nav.search({ sheet: 'cancel', row: input.id }),
});
export const installmentTransferFromList = action({
  id: 'installment.transferFromList',
  kind: 'draft',
  label: msg`Transferir`,
  description: msg`Abre a transferência de carteira de um parcelamento da lista. Nada é transferido sem a confirmação da pessoa.`,
  input: rowInput,
  run: ({ nav, input }) => nav.search({ sheet: 'transfer', row: input.id }),
});
export const installmentRegisterFromList = action({
  id: 'installment.registerFromList',
  kind: 'draft',
  label: msg`Registrar`,
  description: msg`Abre o registro de um parcelamento da lista ainda não registrado. Nada é registrado sem a confirmação da pessoa.`,
  input: rowInput,
  run: ({ nav, input }) => nav.search({ sheet: 'register', row: input.id }),
});
export const installmentEditFromList = action({
  id: 'installment.editFromList',
  kind: 'draft',
  label: msg`Edição rápida`,
  description: msg`Abre a edição rápida do e-mail do pagador de um parcelamento já registrado. Nada é salvo sem a confirmação da pessoa.`,
  input: rowInput,
  run: ({ nav, input }) => nav.search({ sheet: 'edit', row: input.id }),
});

export const installmentListRowActions = [
  installmentOpen,
  installmentCancelFromList,
  installmentTransferFromList,
  installmentRegisterFromList,
  installmentEditFromList,
  installmentPage,
];

export const installmentListScreen = defineScreen({
  id: 'installment.list',
  path: INSTALLMENT_PATH,
  title: msg`Parcelamentos`,
  description: msg`Lista dos parcelamentos da carteira, com filtros, Novo parcelamento e, em cada linha, cancelar, transferir, registrar e a edição rápida do e-mail do pagador.`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'receipt-text', section: 'wallet-management' },
  actions: [...installmentListRowActions, installmentFilter, installmentCreate],
});

// ---- Detalhe ----

const sheetAction = (
  id: string,
  sheet: string,
  label: ReturnType<typeof msg>,
  description: ReturnType<typeof msg>,
  kind: 'write' | 'destructive' = 'write',
) =>
  action({
    id,
    kind,
    label,
    description,
    input: emptyInput,
    preview,
    run: ({ nav }) => nav.search({ sheet, billing: undefined }),
  });

export const installmentTransfer = sheetAction(
  'installment.transfer',
  'transfer',
  msg`Transferir`,
  msg`Abre a transferência do parcelamento aberto na tela para outra carteira, com as réguas da carteira de destino. Nada é transferido sem a confirmação da pessoa.`,
);
export const installmentCancel = sheetAction(
  'installment.cancel',
  'cancel',
  msg`Cancelar`,
  msg`Abre o cancelamento do parcelamento aberto na tela. Nada é cancelado sem a confirmação da pessoa.`,
  'destructive',
);
export const installmentCommunication = action({
  id: 'installment.communication',
  kind: 'write',
  permission: can('BillingAccount', 'Update'),
  label: msg`Atualizar régua de comunicação`,
  description: msg`Abre a troca da régua de comunicação do parcelamento aberto na tela. Nada muda sem a confirmação da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'communication', billing: undefined }),
});
export const installmentSimulate = action({
  id: 'installment.simulate',
  kind: 'write',
  stepUp: true,
  label: msg`Simular e gerar pagamento`,
  description: msg`Abre a simulação de antecipação do parcelamento aberto na tela (total, parcial ou só os atrasos), sobre as cobranças selecionadas quando houver. Gerar o pagamento pede senha e código MFA da pessoa.`,
  input: emptyInput,
  preview,
  run: ({ nav }) => nav.search({ sheet: 'simulate', billing: undefined }),
});
export const installmentRegister = sheetAction(
  'installment.register',
  'register',
  msg`Registrar`,
  msg`Abre o registro do parcelamento aberto na tela, ainda não registrado, com a opção de registrar também as cobranças vencidas. Nada é registrado sem a confirmação da pessoa.`,
);
export const installmentSettle = sheetAction(
  'installment.settle',
  'settle',
  msg`Baixar cobranças`,
  msg`Abre a baixa das cobranças do parcelamento aberto na tela, com data, valor e canal do pagamento. Nada é baixado sem a confirmação da pessoa.`,
);
export const installmentViewBillings = action({
  id: 'installment.viewBillings',
  kind: 'navigate',
  label: msg`Ver cobranças`,
  description: msg`Abre a lista de Cobranças filtrada pelo parcelamento aberto na tela.`,
  input: emptyInput,
  run: ({ nav }) => nav.to(BILLING_PATH),
});
export const installmentPredecessor = action({
  id: 'installment.predecessor',
  kind: 'navigate',
  label: msg`Ver parcelamento anterior`,
  description: msg`Abre o parcelamento que deu origem ao parcelamento aberto na tela, quando existe.`,
  input: emptyInput,
  run: ({ nav }) => nav.to(INSTALLMENT_PATH),
});
export const installmentSuccessor = action({
  id: 'installment.successor',
  kind: 'navigate',
  label: msg`Ver parcelamento sucessor`,
  description: msg`Abre o parcelamento que sucedeu o parcelamento aberto na tela, quando existe.`,
  input: emptyInput,
  run: ({ nav }) => nav.to(INSTALLMENT_PATH),
});
export const installmentCreditNote = action({
  id: 'installment.creditNote',
  kind: 'navigate',
  label: msg`Ver operação de crédito`,
  description: msg`Abre a operação de crédito que originou o parcelamento aberto na tela, quando existe.`,
  input: emptyInput,
  run: ({ nav }) => nav.to('/ccb/operacoes'),
});

// Cobranças do parcelamento (lista dentro do detalhe).
export const installmentBillingOpen = action({
  id: 'installment.billingOpen',
  kind: 'navigate',
  label: msg`Abrir cobrança`,
  description: msg`Abre a ficha de uma cobrança do parcelamento. Nada muda só por abrir.`,
  input: rowInput,
  run: ({ nav, input }) => nav.to(`${BILLING_PATH}/${input.id}`),
});
export const installmentBillingsPage = listPageAction('installment.billingsPage');
export const installmentBillingsFilter = filterAction('installment.billingsFilter', installmentBillingFilters);
export const installmentBillingsSelect = action({
  id: 'installment.billingsSelect',
  kind: 'draft',
  label: msg`Selecionar cobranças`,
  description: msg`Marca cobranças do parcelamento, mesmo em páginas diferentes, e mostra a soma. A simulação de pagamento considera só as selecionadas.`,
  input: z.strictObject({ ids: z.array(z.string()) }),
  run: ({ nav, input }) => nav.search({ selected: input.ids }),
});
const bankSlipDraft = (id: string, sheet: string, label: ReturnType<typeof msg>, description: ReturnType<typeof msg>) =>
  action({
    id,
    kind: 'draft',
    label,
    description,
    input: billingInput,
    run: ({ nav, input }) => nav.search({ sheet, billing: input.billingId }),
  });
export const installmentBankSlipGenerate = bankSlipDraft(
  'installment.bankSlipGenerate',
  'bankSlipGenerate',
  msg`Gerar boleto`,
  msg`Abre a geração do boleto de uma cobrança do parcelamento ainda sem boleto. Nada é gerado sem a confirmação da pessoa.`,
);
export const installmentBankSlipDownload = bankSlipDraft(
  'installment.bankSlipDownload',
  'bankSlipDownload',
  msg`Baixar boleto`,
  msg`Abre o download do boleto de uma cobrança do parcelamento; na vencida, a pessoa escolhe o valor original ou o atualizado.`,
);
export const installmentBankSlipEmail = bankSlipDraft(
  'installment.bankSlipEmail',
  'bankSlipEmail',
  msg`Enviar boleto por e-mail`,
  msg`Abre o envio do boleto de uma cobrança do parcelamento por e-mail, com os destinatários para conferir. Nada é enviado sem a confirmação da pessoa.`,
);
export const installmentBankSlipCancel = bankSlipDraft(
  'installment.bankSlipCancel',
  'bankSlipCancel',
  msg`Registrar baixa`,
  msg`Abre a solicitação de baixa do boleto de uma cobrança do parcelamento. Nada é baixado sem a confirmação da pessoa.`,
);
export const installmentBankSlipDueDate = bankSlipDraft(
  'installment.bankSlipDueDate',
  'bankSlipDueDate',
  msg`Prorrogar vencimento`,
  msg`Abre a prorrogação do vencimento do boleto de uma cobrança do parcelamento. Nada muda sem a confirmação da pessoa.`,
);

export const installmentBillingsActions = [
  installmentBillingOpen,
  installmentBillingsPage,
  installmentBillingsSelect,
  installmentBankSlipGenerate,
  installmentBankSlipDownload,
  installmentBankSlipEmail,
  installmentBankSlipCancel,
  installmentBankSlipDueDate,
];

/** As ações com efeito do parcelamento aberto, na ordem do cabeçalho do legado (InstallmentFormHeader.tsx:86-172). */
export const installmentDetailEffects = [
  installmentTransfer,
  installmentCancel,
  installmentCommunication,
  installmentSimulate,
  installmentRegister,
  installmentSettle,
];

export const installmentDetailNavigation = [
  installmentPredecessor,
  installmentSuccessor,
  installmentCreditNote,
  installmentViewBillings,
];

export const installmentDetailScreen = defineScreen({
  id: 'installment.detail',
  path: `${INSTALLMENT_PATH}/$installmentId`,
  title: msg`Parcelamento`,
  description: msg`Ficha de um parcelamento: carteira, contratos, devedor, réguas, taxas e as cobranças dele, com transferir, cancelar, régua de comunicação, simular e gerar pagamento, registrar, ver cobranças e baixar cobranças.`,
  permission: can('BillingAccount', 'Read'),
  place: { icon: 'receipt-text', parent: 'installment.list', record: msg`Parcelamento` },
  actions: [...installmentDetailNavigation, ...installmentDetailEffects, ...installmentBillingsActions, installmentBillingsFilter],
});

// ---- Contexto do agente (U46 D2-C): sem pagador, documento nem contrato ----

export function installmentListContext(input: { readonly page: number; readonly total: number | undefined; readonly filters: number }): ScreenContext {
  const { page, total, filters } = input;
  return {
    summary: [
      t`Parcelamentos, página ${page}.`,
      total === undefined ? t`Carregando.` : t`Parcelamentos encontrados: ${total}.`,
      filters > 0 ? t`Filtros aplicados: ${filters}.` : t`Sem filtros.`,
    ].join(' '),
    fields: { page, total: total ?? null, filters },
    sensitivity: 'internal',
  };
}

export function installmentDetailContext(
  row: { readonly id: string; readonly statusValue: string; readonly statusDisplay?: string | null | undefined; readonly billingCount?: number | null | undefined; readonly registered?: boolean | null | undefined } | undefined,
  selected: number,
): ScreenContext {
  if (!row) return { summary: t`Parcelamento carregando.`, fields: { status: null }, sensitivity: 'internal' };
  const status = INSTALLMENT_STATUS.label(i18n, row.statusValue, row.statusDisplay ?? row.statusValue) ?? row.statusValue;
  const count = row.billingCount ?? 0;
  return {
    summary: [
      t`Parcelamento aberto, com status ${status} e ${count} parcelas.`,
      row.registered ? t`Já registrado.` : t`Ainda não registrado.`,
      selected > 0 ? t`Cobranças selecionadas: ${selected}.` : '',
    ]
      .filter(Boolean)
      .join(' '),
    entity: { type: 'Installment', id: row.id },
    fields: { status: row.statusValue, billingCount: count, registered: row.registered === true, selectedCount: selected },
    sensitivity: 'internal',
  };
}
